import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { X, Paperclip, FileText, Settings } from '../../icons';
import {
  createTask, addTaskAttachment,
  TASK_STATUSES, TASK_STATUS_META, TASK_PRIORITIES, TASK_PRIORITY_META,
  type TaskStatus, type TaskPriority,
} from '../../api/tasks';
import { uploadChatAttachment, type UploadedAttachment } from '../../api/files';
import { listTeamMembers, type TeamMemberDto } from '../../api/project-teams';
import { stripColor } from './ProjectTopBar';
import { AttachmentTypeIcon } from './AttachmentTypeIcon';
import { Combobox, TaskStatusIcon, AutoGrowTextarea } from '../../components/shared';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';

interface TaskCreateModalProps {
  projectId: string;
  /** v2.9.19 — 담당자 후보(팀원만). 프로젝트의 소속 팀 id, 무소속(개인)=null. 미전달 시 기존 동작 무변경. */
  teamId?: string | null;
  initialStatus: TaskStatus;
  onClose: () => void;
  onCreated: () => void;
  isLeaving?: boolean;
}

/** 마감일 빠른 칩 — 로컬 기준 YYYY-MM-DD. */
function localDateStr(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
function dueIn(days: number, months = 0): string {
  const d = new Date();
  if (months > 0) d.setMonth(d.getMonth() + months);
  if (days > 0) d.setDate(d.getDate() + days);
  return localDateStr(d);
}

/**
 * TaskCreateModal (P3-TASK-FE-4) — 단순화 — 제목/priority/status/due/tags.
 * v2.9.19 — assignees 통합: 팀원만 멀티 선택 칩 (상세 모달 v2.9.17 동형).
 *
 * isLeaving: useModalLeave hook 의 unmount transition 단계 표시 (CSS --leaving
 * 클래스 토글). adr-20260506-modal-unmount-transition-css-only-pattern §2.2.
 *
 * v2.6.1 (2026-06-11) 상세화 — 프로젝트 모달(v2.6)과 동형: 기본 정보/상세 설정
 * 섹션 그룹 + 제목 필수*·카운터·placeholder + 마감일 빠른 칩 + 태그 칩 미리보기 +
 * 푸터 버튼 규격(보조 좌 ghost / 주 우 primary). 제출 시 검증(빈 제목 에러) 유지 —
 * disabled 로 바꾸면 focus trap 순서(마지막 focusable=제출)가 깨짐.
 */
export function TaskCreateModal({ projectId, teamId = null, initialStatus, onClose, onCreated, isLeaving = false }: TaskCreateModalProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
  const [status, setStatus] = useState<TaskStatus>(initialStatus);
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // v2.8 — 파일 첨부: 선택 즉시 presigned 업로드, 생성 성공 후 task 에 연결.
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploading, setUploading] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // v2.9.19 — 담당자: 팀원만 멀티 선택 가능 (상세 모달 v2.9.17 동형). 무소속 프로젝트는 지정 불가 안내.
  const [assignees, setAssignees] = useState<string[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberDto[]>([]);
  useEffect(() => {
    if (!teamId) { setTeamMembers([]); return; }
    let cancelled = false;
    void listTeamMembers(teamId)
      .then((members) => { if (!cancelled) setTeamMembers(members ?? []); })
      .catch(() => { /* 후보 로드 실패 — 담당자 없이 생성 가능 유지 */ });
    return () => { cancelled = true; };
  }, [teamId]);
  function toggleAssignee(userId: string) {
    setAssignees((prev) => prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]);
  }

  async function handleFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    const room = 20 - attachments.length;
    const targets = Array.from(files).slice(0, Math.max(0, room));
    setUploading((n) => n + targets.length);
    for (const f of targets) {
      try {
        const up = await uploadChatAttachment(f);
        setAttachments((prev) => (prev.some((a) => a.fileId === up.fileId) ? prev : [...prev, up]));
      } catch {
        setError(tTask('task.attach.err', { name: f.name }));
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const statusOptions = useMemo(
    () => TASK_STATUSES.map((s) => ({
      value: s,
      label: TASK_STATUS_META[s].label,
    })),
    [],
  );
  const priorityOptions = useMemo(
    () => TASK_PRIORITIES.map((p) => ({ value: p, label: TASK_PRIORITY_META[p].label })),
    [],
  );
  // v2.6.1 — 쉼표 파싱 결과를 칩으로 실시간 미리보기 (submit 시 동일 값 사용)
  const parsedTags = useMemo(
    () => tagsInput.split(',').map((s) => s.trim()).filter((s) => s.length > 0).slice(0, 20),
    [tagsInput],
  );

  useEscapeClose(onClose, submitting);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError(tTask('task.create.err_title'));
      return;
    }
    const tags = parsedTags;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createTask(projectId, {
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        status,
        dueDate: dueDate || undefined,
        tags: tags.length > 0 ? tags : undefined,
        assigneeUserIds: assignees.length > 0 ? assignees : undefined,
      });
      // v2.8 — 업로드된 파일을 태스크에 연결 (개별 실패는 생성 자체를 막지 않음).
      if (attachments.length > 0) {
        await Promise.allSettled(attachments.map((a) => addTaskAttachment(created.id, a.fileId)));
      }
      onCreated();
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tTask('task.create.err_save_fallback')}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={`task-modal-backdrop${isLeaving ? ' task-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting && !isLeaving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tTask('task.create.aria')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`task-modal task-modal--detail task-modal--strip${isLeaving ? ' task-modal--leaving' : ''}`}>
        {/* v2.8.1 — 프로젝트 컬러 스트립 (상단 카드 바 C안 동형) */}
        <span className="task-modal-strip" style={{ background: stripColor(projectId) }} aria-hidden="true" />
        <header className="task-modal-header">
          <h2>{tTask('task.create.heading')}</h2>
          <button type="button" className="task-modal-close" onClick={onClose} disabled={submitting} aria-label={tTask('task.btn.close')} title={tTask('task.btn.close')}>
            <X size={18} />
          </button>
        </header>

        <form id="task-create-form" className="task-modal-body" onSubmit={handleSubmit}>
          {/* ── 기본 정보 ── */}
          <section className="task-form-section" aria-label={tTask('task.create.section_basic')}>
            <h3 className="task-form-section-head">
              <span className="task-section-icon task-section-icon--blue" aria-hidden="true"><FileText size={13} /></span>
              {tTask('task.create.section_basic')}
            </h3>
            <div className="task-modal-field">
              <div className="task-field-head">
                <label htmlFor="task-title">
                  {tTask('task.create.label_title')}
                  <em className="task-req" aria-hidden="true">*</em>
                </label>
                <span className="task-field-counter" aria-label={tTask('task.create.title_counter_aria')}>
                  {title.length}/200
                </span>
              </div>
              <input
                id="task-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={tTask('task.create.title_placeholder')}
                maxLength={200}
                required
                aria-required="true"
                autoFocus
              />
            </div>
            <div className="task-modal-field">
              <label htmlFor="task-description">{tTask('task.create.label_description')}</label>
              <AutoGrowTextarea
                id="task-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={tTask('task.create.desc_placeholder')}
                rows={3}
                maxLength={5000}
              />
            </div>
          </section>

          {/* ── 상세 설정 ── */}
          <section className="task-form-section" aria-label={tTask('task.create.section_detail')}>
            <h3 className="task-form-section-head">
              <span className="task-section-icon task-section-icon--violet" aria-hidden="true"><Settings size={13} /></span>
              {tTask('task.create.section_detail')}
            </h3>
            <div className="task-modal-field-row">
              <div className="task-modal-field">
                <span id="task-status-label">{tTask('task.create.label_status')}</span>
                <Combobox
                  value={status}
                  onChange={(v) => setStatus(v as TaskStatus)}
                  options={statusOptions}
                  renderOption={(o) => (
                    <span className="task-status-option">
                      <TaskStatusIcon status={o.value as TaskStatus} size={12} />
                      {o.label}
                    </span>
                  )}
                  ariaLabel={tTask('task.create.label_status')}
                  disabled={submitting}
                />
              </div>
              <div className="task-modal-field">
                <span id="task-priority-label">{tTask('task.create.label_priority')}</span>
                <Combobox
                  value={priority}
                  onChange={(v) => setPriority(v as TaskPriority)}
                  options={priorityOptions}
                  ariaLabel={tTask('task.create.label_priority')}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="task-modal-field">
              <label htmlFor="task-due">{tTask('task.create.label_due')}</label>
              <div className="task-due-row">
                <input
                  id="task-due"
                  type="date"
                  value={dueDate}
                  min={localDateStr(new Date())}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <span className="task-due-chips" role="group" aria-label={tTask('task.create.due_quick_aria')}>
                  {([
                    { key: '1w', label: tTask('task.create.due_quick_1w'), value: dueIn(7) },
                    { key: '2w', label: tTask('task.create.due_quick_2w'), value: dueIn(14) },
                    { key: '1m', label: tTask('task.create.due_quick_1m'), value: dueIn(0, 1) },
                  ]).map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={`task-due-chip${dueDate === c.value ? ' task-due-chip--on' : ''}`}
                      onClick={() => setDueDate(c.value)}
                      disabled={submitting}
                    >
                      {c.label}
                    </button>
                  ))}
                  {dueDate && (
                    <button type="button" className="task-due-chip task-due-chip--clear" onClick={() => setDueDate('')} disabled={submitting}>
                      {tTask('task.create.due_clear')}
                    </button>
                  )}
                </span>
              </div>
            </div>

            {/* v2.9.19 — 담당자: 팀원만 멀티 선택 (상세 모달 v2.9.17 칩 동형, CSS 재사용) */}
            <div className="task-modal-field">
              <span>{tTask('task.detail.assignee_label')}</span>
              {teamId === null ? (
                <span className="task-detail-hint">{tTask('task.detail.assignee_no_team')}</span>
              ) : teamMembers.length === 0 ? (
                <span className="task-detail-hint">{tTask('task.detail.assignee_empty')}</span>
              ) : (
                <span className="task-assignee-chips" role="group" aria-label={tTask('task.detail.assignee_label')}>
                  {teamMembers.map((m) => {
                    const on = assignees.includes(m.userId);
                    return (
                      <button
                        key={m.userId}
                        type="button"
                        className={`task-assignee-chip${on ? ' task-assignee-chip--on' : ''}`}
                        onClick={() => toggleAssignee(m.userId)}
                        disabled={submitting}
                        aria-pressed={on}
                      >
                        {m.avatarUrl ? (
                          <img className="task-assignee-avatar" src={m.avatarUrl} alt="" />
                        ) : (
                          <span className="task-assignee-avatar task-assignee-avatar--initial" aria-hidden="true">{m.name.slice(0, 1)}</span>
                        )}
                        {m.name}
                      </button>
                    );
                  })}
                </span>
              )}
            </div>

            <div className="task-modal-field">
              <label htmlFor="task-tags">{tTask('task.create.label_tags')}</label>
              <input
                id="task-tags"
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder={tTask('task.create.placeholder_tags')}
              />
              {parsedTags.length > 0 && (
                <span className="task-tag-preview" role="group" aria-label={tTask('task.create.tags_preview_aria')}>
                  {parsedTags.map((t) => (
                    <span key={t} className="task-tag-preview-chip">{t}</span>
                  ))}
                </span>
              )}
            </div>

          </section>

          {/* ── 파일 첨부 (v2.8.4 — 독립 섹션, 상세 모달 동형) ── */}
          <section className="task-form-section" aria-label={tTask('task.attach.label')}>
            <h3 className="task-form-section-head">
              <span className="task-section-icon task-section-icon--amber" aria-hidden="true"><Paperclip size={13} /></span>
              {attachments.length > 0
                ? `${tTask('task.detail.attachments')} (${attachments.length})`
                : tTask('task.attach.label')}
            </h3>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => void handleFilesSelected(e.target.files)}
              />
              <div className="task-attach-row">
                <button
                  type="button"
                  className="task-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting || attachments.length >= 20}
                >
                  <Paperclip size={13} />
                  {tTask('task.attach.add')}
                </button>
                {uploading > 0 && <span className="task-attach-uploading">{tTask('task.attach.uploading')}</span>}
              </div>
              {attachments.length > 0 && (
                <ul className="task-attach-list">
                  {attachments.map((a) => (
                    <li key={a.fileId} className="task-attach-item">
                      <AttachmentTypeIcon mimeType={a.mimeType} name={a.name} />
                      <span className="task-attach-name">{a.name}</span>
                      <span className="task-attach-size">{Math.max(1, Math.round(a.sizeBytes / 1024))}KB</span>
                      <button
                        type="button"
                        className="task-attach-remove"
                        onClick={() => setAttachments((prev) => prev.filter((x) => x.fileId !== a.fileId))}
                        disabled={submitting}
                      >
                        {tTask('task.attach.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <span className="task-detail-hint">{tTask('task.attach.hint')}</span>
          </section>

          {error && <div className="task-modal-error" role="alert">{error}</div>}
        </form>

        {/* 하단 버튼 — 스크롤 본문 밖(모달 맨 하단 고정). 제출은 form 속성으로 연결. */}
        <footer className="task-modal-footer task-modal-footer--split">
          <button type="button" className="task-btn-ghost" onClick={onClose} disabled={submitting}>
            {tTask('task.btn.cancel')}
          </button>
          <button type="submit" form="task-create-form" disabled={submitting} className="task-modal-submit task-btn-primary">
            {submitting ? tTask('task.btn.saving') : tTask('task.create.btn_create')}
          </button>
        </footer>
      </div>
    </div>
  );
}
